import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Globe,
    ShieldCheck,
    Lock,
    CheckCircle2,
    RefreshCw,
    Search,
    Cpu,
    Eye
} from 'lucide-react';
import { backendURL } from '../config';
import AbuseIPDBPopover from '../components/AbuseIPDBPopover';

export default function AttackSurfaceMonitor() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState("perimeter"); // perimeter | ports | ssl | dns | darkweb
    const [rescanning, setRescanning] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/soc/attack-surface`);
            if (res.data && res.data.success) {
                setData(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch attack surface data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleRescan = () => {
        setRescanning(true);
        setTimeout(() => {
            setRescanning(false);
            fetchData();
        }, 1500);
    };

    if (loading && !data) {
        return (
            <div className="min-h-screen bg-[#070b14] flex items-center justify-center text-slate-400 font-mono text-xs">
                <RefreshCw className="w-5 h-5 animate-spin mr-2 text-cyan-400" />
                Scanning External Perimeter &amp; Dark Web Threat Feeds...
            </div>
        );
    }

    const summary = data?.summary || {};
    const perimeterAssets = data?.perimeterAssets || [];
    const dnsHygiene = data?.dnsHygiene || {};
    const darkWebBreaches = data?.darkWebBreaches || [];

    return (
        <div className="min-h-screen bg-[#070b14] text-slate-100 p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.08] pb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-cyan-600/10 border border-cyan-500/20 text-cyan-400">
                        <Globe className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h1 className="text-xl font-black text-slate-100 tracking-tight">
                                External Attack Surface &amp; Dark Web Monitor
                            </h1>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-mono">
                                EASM POSTURE
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-mono">
                                ZERO EXPOSED RDP/SSH
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                            Automated Discovery: Public Perimeter Gateways, Open Port Scanning, SSL/TLS Expiry, DNS Hygiene &amp; Leaked Employee Credentials
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        onClick={handleRescan}
                        disabled={rescanning}
                        className="px-3.5 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-xl text-xs font-bold text-white transition-all flex items-center gap-2 shadow-lg shadow-cyan-600/20 font-mono"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${rescanning ? 'animate-spin' : ''}`} />
                        <span>{rescanning ? 'Probing Perimeter...' : 'Rescan Attack Surface'}</span>
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3.5">
                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 font-mono">External Assets</span>
                    <p className="text-2xl font-black text-slate-100">{summary.totalExternalAssets ?? perimeterAssets.length}</p>
                    <span className="text-[10px] text-cyan-400 font-mono">Discovered Gateways</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 font-mono">Open Services</span>
                    <p className="text-2xl font-black text-emerald-400">{summary.discoveredOpenPorts ?? 0}</p>
                    <span className="text-[10px] text-slate-400 font-mono">TLS 1.3 / HTTPS</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 font-mono">Exposed RDP / SSH</span>
                    <p className="text-2xl font-black text-emerald-400">{summary.criticalExposedPorts ?? 0}</p>
                    <span className="text-[10px] text-emerald-400 font-mono">100% Perimeter Blocked</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 font-mono">Avg SSL Validity</span>
                    <p className="text-2xl font-black text-blue-400">{summary.averageSslDaysRemaining ?? 0}d</p>
                    <span className="text-[10px] text-slate-400 font-mono">A+ Security Grade</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 font-mono">DNS &amp; DMARC</span>
                    <p className="text-2xl font-black text-purple-400">{summary.dnsSecurityScore ?? 0}%</p>
                    <span className="text-[10px] text-purple-400 font-mono">p=reject Enforced</span>
                </div>

                <div className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 font-mono">Dark Web Alerts</span>
                    <p className="text-2xl font-black text-amber-400">{summary.activeCredentialAlerts ?? darkWebBreaches.length}</p>
                    <span className="text-[10px] text-amber-400 font-mono">{summary.activeCredentialAlerts ?? darkWebBreaches.length} Under Monitoring</span>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex items-center gap-2 border-b border-white/[0.08] pb-3 text-xs font-mono">
                <button
                    onClick={() => setActiveTab("perimeter")}
                    className={`px-3 py-1.5 rounded-lg transition-all ${activeTab === 'perimeter' ? 'bg-cyan-500/20 text-cyan-300 font-bold border border-cyan-500/30' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    Discovered Perimeter Gateways ({perimeterAssets.length})
                </button>
                <button
                    onClick={() => setActiveTab("dns")}
                    className={`px-3 py-1.5 rounded-lg transition-all ${activeTab === 'dns' ? 'bg-purple-500/20 text-purple-300 font-bold border border-purple-500/30' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    DNS &amp; Email Hygiene
                </button>
                <button
                    onClick={() => setActiveTab("darkweb")}
                    className={`px-3 py-1.5 rounded-lg transition-all ${activeTab === 'darkweb' ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/30' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    Dark Web Credential Watch ({darkWebBreaches.length})
                </button>
            </div>

            {/* Tab 1: Discovered Perimeter Gateways */}
            {activeTab === 'perimeter' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {perimeterAssets.map(asset => (
                        <div key={asset.id} className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-4 flex flex-col justify-between">
                            <div className="space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-mono font-bold text-cyan-400">{asset.id}</span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                        {asset.status}
                                    </span>
                                </div>

                                <div>
                                    <h2 className="text-sm font-bold text-slate-100">{asset.hostname}</h2>
                                    <span className="text-xs text-slate-400">{asset.type}</span>
                                </div>

                                <div className="p-2.5 rounded-lg bg-black/40 border border-white/[0.04] flex items-center justify-between text-xs font-mono">
                                    <span className="text-slate-400">Public IP:</span>
                                    <AbuseIPDBPopover ip={asset.ip} showBadge={true} className="text-blue-400 hover:underline">
                                        {asset.ip}
                                    </AbuseIPDBPopover>
                                </div>

                                {/* SSL Certificate Health */}
                                <div className="p-2.5 rounded-lg bg-black/40 border border-white/[0.04] space-y-1 text-xs font-mono">
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-400">SSL Grade:</span>
                                        <span className="text-emerald-400 font-bold">{asset.sslGrade}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-400">Expires in:</span>
                                        <span className="text-slate-200">{asset.sslExpiresDays} days ({asset.sslIssuer})</span>
                                    </div>
                                </div>

                                {/* Port Scan Results */}
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">Open Services:</span>
                                    <div className="space-y-1">
                                        {asset.openPorts.map((p, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-[11px] font-mono p-1.5 rounded bg-white/[0.02]">
                                                <span className="text-slate-200">{p.port}/{p.protocol} ({p.service})</span>
                                                <span className="text-emerald-400 font-bold">{p.state}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Filtered / Blocked Ports */}
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">Perimeter Firewall Protection:</span>
                                    <div className="space-y-1">
                                        {asset.blockedPorts.map((p, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-[11px] font-mono p-1.5 rounded bg-rose-500/5 border border-rose-500/10">
                                                <span className="text-slate-300">Port {p.port} ({p.service})</span>
                                                <span className="text-rose-400 font-bold text-[10px]">{p.state}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="pt-2 border-t border-white/[0.04] flex items-center justify-between text-[10px] font-mono text-slate-500">
                                <span>Last probed: {new Date(asset.lastScanned).toLocaleTimeString()}</span>
                                <span className="text-cyan-400">Continuous Monitor</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tab 2: DNS & Email Hygiene */}
            {activeTab === 'dns' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-4">
                        <h2 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-purple-400" />
                            <span>Sender Policy Framework (SPF) &amp; DKIM</span>
                        </h2>

                        <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-1.5 text-xs font-mono">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">SPF Record Status:</span>
                                <span className="text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded">{dnsHygiene.spf?.status}</span>
                            </div>
                            <pre className="text-[11px] text-slate-300 p-2 rounded bg-white/[0.02] overflow-x-auto">
                                {dnsHygiene.spf?.record}
                            </pre>
                        </div>

                        <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-1.5 text-xs font-mono">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">DKIM Authentication:</span>
                                <span className="text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded">{dnsHygiene.dkim?.status}</span>
                            </div>
                            <span className="text-slate-300 block text-[11px]">Selector: {dnsHygiene.dkim?.selector} (RSA {dnsHygiene.dkim?.rsaBits}-bit)</span>
                        </div>
                    </div>

                    <div className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-4">
                        <h2 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                            <Lock className="w-4 h-4 text-purple-400" />
                            <span>DMARC Enforcement &amp; DNSSEC</span>
                        </h2>

                        <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-1.5 text-xs font-mono">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">DMARC Policy Enforcement:</span>
                                <span className="text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded">{dnsHygiene.dmarc?.status}</span>
                            </div>
                            <pre className="text-[11px] text-slate-300 p-2 rounded bg-white/[0.02] overflow-x-auto">
                                {dnsHygiene.dmarc?.record}
                            </pre>
                        </div>

                        <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-1.5 text-xs font-mono">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">DNSSEC Integrity:</span>
                                <span className="text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded">{dnsHygiene.dnssec?.status}</span>
                            </div>
                            <span className="text-slate-300 block text-[11px]">Algorithm: {dnsHygiene.dnssec?.algorithm}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Tab 3: Dark Web Credential Breach Watch */}
            {activeTab === 'darkweb' && (
                <div className="bg-[#0d1424] border border-white/[0.06] rounded-xl overflow-hidden space-y-3">
                    <div className="p-4 border-b border-white/[0.06] flex items-center justify-between bg-[#11192e]/40">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono">
                                Corporate Domain Credential Breach Feed (@querytel.com)
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-400 font-mono">
                                {darkWebBreaches.length} Exposures Logged
                            </span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400">Updated from Dark Web Paste &amp; Stealer Archives</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs">
                            <thead>
                                <tr className="border-b border-white/[0.06] bg-[#070b14]/50 text-[10px] uppercase font-bold tracking-wider text-slate-400 font-mono">
                                    <th className="px-4 py-3">Compromised Email</th>
                                    <th className="px-4 py-3">Breach Intelligence Source</th>
                                    <th className="px-4 py-3">Discovered On</th>
                                    <th className="px-4 py-3">Leaked Data Types</th>
                                    <th className="px-4 py-3">Breach Date</th>
                                    <th className="px-4 py-3">Risk Rating</th>
                                    <th className="px-4 py-3 text-right">Remediation Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {darkWebBreaches.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-16 text-center">
                                            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                                                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                                            </div>
                                            <h3 className="text-sm font-bold text-slate-200">Zero Compromised Accounts Detected</h3>
                                            <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                                                All monitored QueryTel corporate accounts and domains are clean across monitored dark web sources, stealer logs, and Tor paste repositories.
                                            </p>
                                            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono font-semibold">
                                                <ShieldCheck className="w-3.5 h-3.5" />
                                                ENTRA ID TENANT VERIFIED CLEAN
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    darkWebBreaches.map(breach => (
                                        <tr key={breach.id} className="hover:bg-white/[0.03] transition-colors">
                                            <td className="px-4 py-3 font-semibold text-slate-200 font-mono">
                                                {breach.email}
                                            </td>
                                        <td className="px-4 py-3 text-slate-300 font-medium">
                                            {breach.source}
                                        </td>
                                        {/* Discovery Source Column */}
                                        <td className="px-4 py-3">
                                            <div className="space-y-1.5">
                                                <div className="flex items-center gap-1.5">
                                                    <Eye className="w-3 h-3 text-amber-400 shrink-0" />
                                                    <span className="text-slate-200 font-bold text-[11px]">
                                                        {breach.discoveryPlatform || 'Unknown Platform'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400">
                                                    <Search className="w-2.5 h-2.5 shrink-0" />
                                                    <span>{breach.discoveryMethod || 'Manual Discovery'}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
                                                    <Cpu className="w-2.5 h-2.5 shrink-0" />
                                                    <span>{breach.discoveryEngine || 'QueryTel DarkEye'}</span>
                                                </div>
                                                {breach.discoveryUrl && (
                                                    <div className="flex items-center gap-1.5 mt-1 p-1.5 rounded-lg bg-rose-500/5 border border-rose-500/10">
                                                        <Lock className="w-2.5 h-2.5 text-rose-400 shrink-0" />
                                                        <span className="text-[9px] font-mono text-slate-400 truncate max-w-[140px]" title={breach.discoveryUrl}>
                                                            {breach.discoveryUrl}
                                                        </span>
                                                        <span className="px-1.5 py-0.5 rounded text-[8px] font-bold font-mono bg-rose-500/20 text-rose-400 border border-rose-500/30 shrink-0 whitespace-nowrap">
                                                            BLOCKED BY FIREWALL
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {breach.leakedData.map((d, i) => (
                                                    <span key={i} className="px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-300 text-[10px] font-mono">
                                                        {d}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-[11px] text-slate-400">
                                            {breach.breachDate}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono ${
                                                breach.riskScore === 'High' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                                breach.riskScore === 'Medium' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                                'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                            }`}>
                                                {breach.riskScore}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono ${
                                                breach.resolved
                                                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                                    : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                            }`}>
                                                {breach.status}
                                            </span>
                                        </td>
                                    </tr>
                                )))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
